import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { calculateInstallments } from './types';
import type { TreatmentPackage, TreatmentPackageWithPatient } from './types';

export function useTreatmentPackages() {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['treatment_packages', clientId],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<TreatmentPackageWithPatient[]> => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('treatment_packages')
        .select('*, patient:patients(id, name, phone, email)')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as TreatmentPackageWithPatient[];
    },
  });
}

export interface CreateTreatmentPackageInput {
  patientId: string;
  description: string;
  totalAmount: number;
  downPayment: number;
  numberOfInstallments: number;
  startDate: string; // YYYY-MM-DD
  accountId: string;
  categoryId?: string | null;
  costCenterId?: string | null;
  notes?: string;
}

/**
 * Cria o pacote e gera as transactions (entrada + parcelas) num único fluxo.
 * Não-atomic em DB: se a inserção das transactions falhar, o pacote é apagado para reverter.
 */
export function useCreateTreatmentPackage() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreateTreatmentPackageInput): Promise<TreatmentPackage> => {
      if (!clientId) throw new Error('Cliente não selecionado');

      const startDate = new Date(`${input.startDate}T00:00:00`);
      const summary = calculateInstallments(
        input.totalAmount,
        input.downPayment,
        input.numberOfInstallments,
        startDate,
      );
      if (summary.installments.length === 0) throw new Error('Gere ao menos uma parcela.');

      // 1) Cria o pacote
      const { data: pkg, error: pkgErr } = await supabase
        .from('treatment_packages')
        .insert({
          client_id: clientId,
          patient_id: input.patientId,
          description: input.description.trim(),
          total_amount: input.totalAmount,
          down_payment: input.downPayment,
          number_of_installments: input.numberOfInstallments,
          start_date: input.startDate,
          account_id: input.accountId,
          category_id: input.categoryId ?? null,
          cost_center_id: input.costCenterId ?? null,
          notes: input.notes?.trim() || null,
        })
        .select()
        .single();
      if (pkgErr) throw pkgErr;
      const pkgRow = pkg as TreatmentPackage;

      // 2) Gera transactions (todas como contas a receber pendentes)
      const recurrenceGroupId = crypto.randomUUID();
      const rows = summary.installments.map((it) => ({
        client_id: clientId,
        type: 'income' as const,
        description: `${input.description} — ${it.label}`,
        amount: it.amount,
        date: format(it.date, 'yyyy-MM-dd'),
        account_id: input.accountId,
        category_id: input.categoryId ?? null,
        cost_center_id: input.costCenterId ?? null,
        status: 'pending' as const,
        is_recurring: summary.installments.length > 1,
        recurrence_type: summary.installments.length > 1 ? 'monthly' : null,
        recurrence_group_id: summary.installments.length > 1 ? recurrenceGroupId : null,
        number_of_repetitions: summary.installments.length > 1 ? summary.installments.length : null,
        treatment_package_id: pkgRow.id,
        notes: input.notes?.trim() || null,
      }));

      const { error: txErr } = await supabase.from('transactions').insert(rows);
      if (txErr) {
        // Rollback manual: apaga o pacote (soft-delete pra ficar no audit_log)
        await supabase
          .from('treatment_packages')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', pkgRow.id);
        throw txErr;
      }
      return pkgRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treatment_packages'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Pacote criado e parcelas geradas.');
    },
    onError: (e: Error) => toast.error(`Erro ao criar pacote: ${e.message}`),
  });
}

export function useDeleteTreatmentPackage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pkg: TreatmentPackage) => {
      const nowIso = new Date().toISOString();
      // Soft delete do pacote
      const { error: pkgErr } = await supabase
        .from('treatment_packages')
        .update({ deleted_at: nowIso })
        .eq('id', pkg.id);
      if (pkgErr) throw pkgErr;
      // Soft delete das transactions ainda pendentes vinculadas
      const { error: txErr } = await supabase
        .from('transactions')
        .update({ deleted_at: nowIso })
        .eq('treatment_package_id', pkg.id)
        .eq('status', 'pending');
      if (txErr) throw txErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['treatment_packages'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Pacote excluído (parcelas pagas mantidas).');
    },
    onError: (e: Error) => toast.error(`Erro ao excluir: ${e.message}`),
  });
}
